import React, { useState, useEffect } from 'react';
import { likeImage, addComment, getComments, incrementView } from '../../../api/api';
import './SelectedImagePanel.css';

const EmbeddedSelectedImagePanel = ({
  selectedImage,
  isAuthenticated,
  onClose,
  onSearch,
}) => {
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(selectedImage?.likes || 0);
  const [viewCount, setViewCount] = useState(selectedImage?.views || 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedImage?.publicId) {
      fetchComments();
      handleViewIncrement();
    }
  }, [selectedImage?.publicId]);

  const handleViewIncrement = async () => {
    try {
      const response = await incrementView(selectedImage.publicId);
      setViewCount(response.data.views);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await getComments(selectedImage.publicId);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      window.location.href = '/sign-in';
      return;
    }

    try {
      const response = await likeImage(selectedImage.publicId);
      setIsLiked(response.data.hasLiked);
      setLikesCount(response.data.likes);
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;

    try {
      setLoading(true);
      await addComment(selectedImage.publicId, newComment);
      setNewComment('');
      // Refresh comments after adding new one
      await fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (selectedImage?.path) {
      const link = document.createElement('a');
      link.href = selectedImage.path;
      link.download = `inspirapix-${selectedImage.publicId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = async () => {
    if (selectedImage?.publicId) {
      const shareUrl = `${window.location.origin}/explore?selected=${selectedImage.publicId}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Check out this image on InspiraPix',
            text: selectedImage.prompt,
            url: shareUrl
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error sharing:', error);
          }
        }
      } else {
        // Fallback to copying to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
          // You might want to show a notification that the URL was copied
          alert('Link copied to clipboard!');
        } catch (error) {
          console.error('Error copying to clipboard:', error);
        }
      }
    }
  };

  if (!selectedImage) return null;

  const handleTagClick = (tag) => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', tag.toLowerCase());
    searchParams.set('page', '1');
    searchParams.set('sortBy', 'relevance');
    searchParams.set('timeRange', 'all');
    searchParams.set('aspectRatio', 'all');
    window.location.href = `/explore?${searchParams.toString()}`;
  };

  return (
    <div className="embedded-panel">
      <button className="panel-close-btn" onClick={onClose}>
        <i className="fas fa-times"></i>
      </button>

      <div className="panel-content">
        <div className="panel-preview">
          <div className="panel-preview-wrapper">
            <img src={selectedImage.path} alt={selectedImage.prompt} />
            <div className="image-actions">
              <button 
                className={`action-btn primary ${isLiked ? 'liked' : ''}`} 
                onClick={handleLike}
                title={isAuthenticated ? 'Like' : 'Sign in to like'}
              >
                <i className={`fas fa-heart ${isLiked ? 'liked' : ''}`}></i>
                <span>{likesCount}</span>
              </button>
              <button className="action-btn secondary" title="Share" onClick={handleShare}>
                <i className="fas fa-share-alt"></i>
              </button>
              <button className="action-btn secondary" title="Download" onClick={handleDownload}>
                <i className="fas fa-cloud-download-alt"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="panel-details">
          <div className="panel-header">
            <div className="creator-info">
              <i className="fas fa-user-circle"></i>
              <span>{selectedImage.creator || 'Anonymous'}</span>
            </div>
          </div>

          <p className="prompt-text">{selectedImage.prompt}</p>
          {selectedImage.tags && selectedImage.tags.length > 0 && (
            <div className="panel-tags">
              {selectedImage.tags.map((tag, idx) => (
                <span key={idx} className="tag" onClick={() => handleTagClick(tag)}>
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <div className="panel-meta">
            <span className="meta-item">
              <i className="fas fa-calendar"></i>
              {new Date(selectedImage.createdAt).toLocaleDateString()}
            </span>
            <span className="meta-item">
              <i className="fas fa-eye"></i>
              {viewCount} views
            </span>
            <span className="meta-item">
              <i className="fas fa-image"></i>
              {selectedImage.pipeline || 'Standard'}
            </span>
          </div>

          <div className="comments-section">
            <h3>Comments</h3>

            {isAuthenticated ? (
              <form onSubmit={handleCommentSubmit} className="comment-form">
                <textarea
                  rows="3"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="✨ Share your thoughts on this masterpiece..."
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="submit-comment-btn"
                  disabled={!newComment.trim() || loading}
                >
                  {loading ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            ) : (
              <div className="signin-prompt">
                <p>
                  Please <a href="/sign-in">sign in</a> to comment
                </p>
              </div>
            )}

            <div className="comments-list">
              {comments.length > 0 ? (
                comments.map((comment, idx) => (
                  <div key={idx} className="comment">
                    <div className="comment-header">
                      <span className="comment-author">
                        <i className="fas fa-user-circle"></i>
                        {comment.userId?.username || 'Anonymous'}
                      </span>
                      <span className="comment-date">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="comment-text">{comment.comment}</p>
                  </div>
                ))
              ) : (
                <p className="no-comments">No comments yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedSelectedImagePanel;
